import { CalendarMonthOutlined, KeyboardArrowDownRounded } from "@mui/icons-material";
import { Button, MenuItem, Popover, Skeleton, Stack } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useGetSurveyInstances } from "../http";

interface IProps {
  surveyGuid: string;
  surveyInstanceGuid: string | undefined;
  hideActiveInstance?: boolean;
  onChange: (surveyInstance: ISurveyInstancePickerOption) => void;
}

interface ISurveyInstancePickerOption {
  guid: string;
  name: string;
  startDateTimeUtc: DateTime;
  endDateTimeUtc?: DateTime;
}

const SurveyInstancePicker = (props: IProps) => {
  const { data, isLoading } = useGetSurveyInstances(props.surveyGuid);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [surveyInstances, setSurveyInstances] = useState<ISurveyInstancePickerOption[]>([]);

  useEffect(() => {
    if (!isLoading && data) {
      const surveyInstances: ISurveyInstancePickerOption[] = data.surveyInstances
        .filter((x) => !props.hideActiveInstance || (x.endDateUtc && x.endDateUtc < DateTime.utc().toISO()))
        .sort((a, b) => b.startDateUtc.localeCompare(a.startDateUtc))
        .map((x) => ({
          guid: x.surveyInstanceGuid,
          name: getSurveyInstanceName(x.startDateUtc, x.endDateUtc),
          startDateTimeUtc: DateTime.fromISO(x.startDateUtc, { zone: "utc" }),
          endDateTimeUtc: x.endDateUtc === undefined ? undefined : DateTime.fromISO(x.endDateUtc, { zone: "utc" }),
        }));

      setSurveyInstances(surveyInstances);
      handleSelectionChanged(surveyInstances[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data]);

  useEffect(() => {
    // If instances are loaded, but the selected survey instance has been cleared, reset it to the default.
    if (surveyInstances.length > 0 && props.surveyInstanceGuid === undefined) {
      handleSelectionChanged(surveyInstances[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, props.surveyInstanceGuid]);

  function getSurveyInstanceName(startDateUtc: string, endDateUtc?: string) {
    const startDate = DateTime.fromISO(startDateUtc, { zone: "utc" });
    const endDate = endDateUtc ? DateTime.fromISO(endDateUtc, { zone: "utc" }) : undefined;
    const formattedStartDate = startDate.toLocal().toLocaleString(DateTime.DATE_MED);
    const formattedEndDate = endDate ? endDate.toLocal().toLocaleString(DateTime.DATE_MED) : "End Date Not Set";
    const name = `${formattedStartDate} - ${formattedEndDate}`;

    return name;
  }

  function handleSelectionChanged(surveyInstance: ISurveyInstancePickerOption) {
    setMenuAnchor(null);
    props.onChange(surveyInstance);
  }

  return (
    <>
      <Button
        variant="tertiary"
        startIcon={<CalendarMonthOutlined />}
        endIcon={<KeyboardArrowDownRounded />}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
      >
        {props.surveyInstanceGuid === undefined
          ? "Select Period"
          : surveyInstances.find((x) => x.guid === props.surveyInstanceGuid)?.name}
      </Button>

      <Popover
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "10px",
            },
          },
        }}
      >
        <Stack sx={{ m: 0.5 }}>
          {isLoading && [...Array(3)].map((_, i) => <Skeleton key={i} />)}

          {!isLoading &&
            data &&
            surveyInstances.map((x, i) => (
              <MenuItem
                key={i}
                selected={x.guid === props.surveyInstanceGuid}
                value={x.guid}
                onClick={() => handleSelectionChanged(x)}
              >
                {x.name}
              </MenuItem>
            ))}
        </Stack>
      </Popover>
    </>
  );
};

export { SurveyInstancePicker };
export type { ISurveyInstancePickerOption };
